import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Brain,
  ArrowLeft,
  FileSearch,
  Shield,
  Zap,
  CheckCircle,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { aiApi } from "@/services/api";

export const AISettings = () => {
  const { t } = useTranslation();
  const [aiEnabled, setAiEnabled] = useState(true);

  // Fetch AI status
  const { data: _status, isLoading: _isLoading } = useQuery({
    queryKey: ["ai-status"],
    queryFn: () => aiApi.getStatus().then((r) => r.data),
    retry: false,
  });

  const features = [
    {
      id: "field-detection",
      name: "AI Field Detection",
      description:
        "Automatically detect signature, date, and form fields in uploaded documents.",
      icon: FileSearch,
      status: "active",
      plan: "All Plans",
    },
    {
      id: "contract-analysis",
      name: "Contract Analysis",
      description:
        "Analyze contracts for key terms, clauses, obligations, and potential risks.",
      icon: Brain,
      status: "active",
      plan: "Business+",
    },
    {
      id: "document-summary",
      name: "Document Summary",
      description:
        "Generate quick summaries of document content and key points.",
      icon: Zap,
      status: "active",
      plan: "Business+",
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link
          to="/integrations"
          className="flex items-center justify-center h-8 w-8 rounded-lg border hover:bg-accent"
          aria-label="Back to integrations"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        </Link>
        <div className="flex items-center gap-3">
          <div>
            <h1 className="text-2xl font-bold font-heading tracking-tight">
              {t("ai.features")}
            </h1>
            <p className="text-muted-foreground">
              Configure AI-powered document analysis and field detection
            </p>
          </div>
        </div>
      </div>

      {/* Status Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CardTitle>{t("ai.processing")}</CardTitle>
              <Badge
                variant="outline"
                className="bg-green-500/10 text-green-600 border-green-200"
              >
                Beta
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              <Label
                htmlFor="ai-enabled"
                className="text-sm text-muted-foreground"
              >
                {aiEnabled ? "Enabled" : "Disabled"}
              </Label>
              <Switch
                id="ai-enabled"
                checked={aiEnabled}
                onCheckedChange={setAiEnabled}
              />
            </div>
          </div>
          <CardDescription>
            AI features help automate document processing and improve accuracy
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Card className="bg-muted/50">
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">
                AI Field Detection analyzes your documents using pattern
                recognition to identify common signature blocks, date fields,
                and form elements. All processing happens securely on our
                servers. No document content is stored or used for training.
              </p>
            </CardContent>
          </Card>
        </CardContent>
      </Card>

      {/* Features */}
      <Card>
        <CardHeader>
          <CardTitle>{t("ai.availableFeatures")}</CardTitle>
          <CardDescription>{t("ai.availableFeaturesDesc")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <div key={feature.id}>
                {index > 0 && <Separator className="my-4" />}
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div
                      className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted"
                      aria-hidden="true"
                    >
                      <Icon className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium">{feature.name}</h3>
                        <Badge variant="secondary" className="text-xs">
                          {feature.plan}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mt-0.5">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle
                      className="h-5 w-5 text-green-600"
                      aria-hidden="true"
                    />
                    <span className="text-sm text-green-600">
                      {t("common.active")}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Privacy & Security */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Shield
              className="h-5 w-5 text-muted-foreground"
              aria-hidden="true"
            />
            <CardTitle>{t("ai.privacySecurity")}</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <CheckCircle
                className="h-5 w-5 text-green-600 mt-0.5 shrink-0"
                aria-hidden="true"
              />
              <div>
                <p className="font-medium">{t("ai.noDataRetention")}</p>
                <p className="text-sm text-muted-foreground">
                  Document content is processed in real-time and not stored
                  after analysis
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle
                className="h-5 w-5 text-green-600 mt-0.5 shrink-0"
                aria-hidden="true"
              />
              <div>
                <p className="font-medium">{t("common.secureProcessing")}</p>
                <p className="text-sm text-muted-foreground">
                  All AI processing happens on encrypted, secure servers
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle
                className="h-5 w-5 text-green-600 mt-0.5 shrink-0"
                aria-hidden="true"
              />
              <div>
                <p className="font-medium">{t("ai.noModelTraining")}</p>
                <p className="text-sm text-muted-foreground">
                  Your documents are never used to train AI models
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Usage */}
      <Card>
        <CardHeader>
          <CardTitle>{t("ai.howToUse")}</CardTitle>
          <CardDescription>{t("ai.howToUseDesc")}</CardDescription>
        </CardHeader>
        <CardContent>
          <ol className="space-y-3">
            <li className="flex items-start gap-3">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm font-medium shrink-0">
                1
              </span>
              <div>
                <p className="font-medium">{t("ai.step1Title")}</p>
                <p className="text-sm text-muted-foreground">
                  {t("ai.step1Desc")}
                </p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm font-medium shrink-0">
                2
              </span>
              <div>
                <p className="font-medium">{t("ai.step2Title")}</p>
                <p className="text-sm text-muted-foreground">
                  {t("ai.step2Desc")}
                </p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm font-medium shrink-0">
                3
              </span>
              <div>
                <p className="font-medium">{t("templates.goToFieldsStep")}</p>
                <p className="text-sm text-muted-foreground">
                  This is where you place signature and form fields on the
                  document
                </p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm font-medium shrink-0">
                4
              </span>
              <div>
                <p className="font-medium">Click the "AI Detect" button</p>
                <p className="text-sm text-muted-foreground">
                  Located in the field toolbar, next to the manual field types
                </p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm font-medium shrink-0">
                5
              </span>
              <div>
                <p className="font-medium">{t("ai.reviewSuggestions")}</p>
                <p className="text-sm text-muted-foreground">
                  AI will analyze your document and suggest field placements.
                  Click to apply them.
                </p>
              </div>
            </li>
          </ol>
        </CardContent>
      </Card>
    </div>
  );
};

export default AISettings;
